// Données pédagogiques : Réforme PCG 2025 (ANC 2022-06)
// Source : fiche pédagogique "La réforme du PCG 2025"

export const REFORME_TLDR = {
  titre: "L'essentiel en 30 secondes",
  texte:
    "La plus large refonte du Plan comptable général depuis 2014. Trois objectifs : simplifier la présentation, moderniser les états financiers et réduire le nombre de comptes (environ 1 600 contre plus de 2 000). Au quotidien, deux changements dominent : le résultat exceptionnel est vidé de presque tout son contenu, et la technique du transfert de charges disparaît.",
};

export const REFORME_POINTS = [
  {
    n: "01",
    titre: "Le résultat exceptionnel est redéfini",
    desc: "Il ne couvre plus que les opérations liées à un événement majeur et inhabituel (catastrophe, expropriation…), quelques écritures purement fiscales et l'effet des changements de méthode. Cessions d'immobilisations, quote-part de subventions, amendes et dons basculent en exploitation ou en financier.",
    color: "#A33B4B",
  },
  {
    n: "02",
    titre: "Les transferts de charges sont supprimés",
    desc: "Plus de comptes 791 / 796 / 797. On enregistre désormais par nature : un remboursement de charges de personnel en 649, une indemnité d'assurance en 7587, etc.",
    color: "#B85724",
  },
  {
    n: "03",
    titre: "Le plan de comptes est allégé",
    desc: "Environ un compte sur cinq disparaît. Les comptes sont répartis entre obligatoires et facultatifs pour gagner en lisibilité et en comparabilité.",
    color: "#A56A2E",
  },
  {
    n: "04",
    titre: "Les états financiers sont modernisés",
    desc: "Le bilan est présenté en tableau actif / passif ; le compte de résultat passe en liste, qui fait apparaître la formation du résultat de haut en bas.",
    color: "#2D6BE0",
  },
  {
    n: "05",
    titre: "L'annexe est restructurée",
    desc: "Des tableaux normalisés (obligatoires ou indicatifs) remplacent le format libre, complétés d'un tableau de synthèse des dispositions applicables à l'entité.",
    color: "#2F7A5C",
  },
];

export const REFORME_TABLE = {
  intro: "Mémo de reclassement. Les opérations ci-dessous quittent le résultat exceptionnel.",
  rows: [
    {
      op: "Cession immo corporelle / incorporelle — VNC",
      avant: "675",
      apres: "657",
      bloc: "Exploitation",
    },
    {
      op: "Cession immo corporelle / incorporelle — prix de cession",
      avant: "775",
      apres: "757",
      bloc: "Exploitation",
    },
    {
      op: "Cession d'immobilisation financière",
      avant: "675 / 775",
      apres: "667 / 767",
      bloc: "Financier",
    },
    {
      op: "Quote-part de subvention d'investissement",
      avant: "777",
      apres: "747",
      bloc: "Exploitation",
    },
    {
      op: "Pénalités, amendes fiscales et pénales",
      avant: "6712",
      apres: "6582",
      bloc: "Gestion courante",
    },
    {
      op: "Pénalités sur marchés et dédits",
      avant: "6711",
      apres: "6581",
      bloc: "Gestion courante",
    },
    {
      op: "Dons et libéralités",
      avant: "6713",
      apres: "6238",
      bloc: "Charges externes",
    },
    {
      op: "Créances devenues irrécouvrables",
      avant: "6714",
      apres: "654",
      bloc: "Gestion courante",
    },
    {
      op: "Transferts de charges",
      avant: "791 / 796 / 797",
      apres: "supprimés",
      bloc: "Par nature",
    },
    {
      op: "Charges / produits réellement exceptionnels",
      avant: "671 / 771",
      apres: "678 / 778 (+ 672 / 772 transit.)",
      bloc: "Exceptionnel",
    },
  ],
};

export const REFORME_ARBRE = {
  titre: "Arbre de décision : exceptionnel ou courant ?",
  intro:
    "Depuis le PCG 2025, il faut répondre OUI aux trois questions pour comptabiliser en résultat exceptionnel. Une seule réponse NON bascule l'opération en résultat courant.",
  questions: [
    "Y a-t-il un événement majeur et inhabituel ?",
    "Les produits et charges sont-ils directement liés à cet événement ?",
    "Sont-ils des produits et charges supplémentaires, qui n'auraient pas été constatés en l'absence de l'événement ?",
  ],
  oui: {
    label: "3 fois OUI",
    sub: "Comptabilisation en résultat EXCEPTIONNEL",
    note: "Cas désormais rare",
  },
  non: {
    label: "Au moins 1 NON",
    sub: "Comptabilisation en résultat COURANT (exploitation ou financier)",
    note: "Cas le plus fréquent depuis 2025",
  },
  apres: {
    titre: "Au-delà de l'arbre, restent aussi exceptionnels",
    items: [
      "Les écritures purement fiscales : provisions réglementées, amortissements dérogatoires.",
      "L'impact des changements de méthode comptable inscrits en résultat.",
    ],
    note: "Ces deux cas suivent leur propre logique fiscale et ne passent pas par l'arbre ci-dessus.",
  },
};

export const REFORME_REFLEXES = [
  {
    titre: "Une cession d'immobilisation n'est plus exceptionnelle",
    desc: "La plus-value se lit dans le résultat d'exploitation (corporel / incorporel) ou financier.",
  },
  {
    titre: "Le réflexe « je neutralise par un 791 » n'existe plus",
    desc: "On comptabilise par nature : remboursement de salaire en 649, indemnité d'assurance en 7587.",
  },
  {
    titre: "Une amende reste non déductible fiscalement",
    desc: "Même si elle change de compte (6712 → 6582), la réintégration extra-comptable demeure.",
  },
];

export const REFORME_QUIZ = [
  {
    q: "Une entreprise vend une machine. Où enregistrer la VNC et le prix de cession ?",
    r: "La VNC en `657` et le prix de cession en `757`, dans le résultat d'exploitation. (Avant la réforme : `675` et `775`, en exceptionnel.)",
  },
  {
    q: "L'OPCO rembourse une formation. Quel compte est désormais interdit ?",
    r: "Le `791` (transfert de charges), supprimé. Le remboursement se comptabilise par nature, en produit.",
  },
  {
    q: "Citez deux familles d'écritures qui restent dans le résultat exceptionnel.",
    r: "Les écritures purement fiscales (provisions réglementées, amortissements dérogatoires) et l'effet des changements de méthode — en plus des événements majeurs et inhabituels.",
  },
];
